import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Wrench, X } from "lucide-react";
import { parts } from "@/data/partsList";
import { partsForStep } from "@/data/stepParts";

/**
 * A brutalist button + modal pair. Renders a "Parts used" button that, when
 * clicked, opens a full-screen overlay listing the checklist parts referenced
 * by the given step (image + label). If `substeps` is provided, only the
 * parts needed for those substeps are shown.
 */
export default function StepPartsButton({ stepNo, substeps }) {
  const [open, setOpen] = useState(false);
  const items = useMemo(() => {
    const ids = partsForStep(stepNo, substeps);
    if (!ids.length) return [];
    const byId = Object.fromEntries(parts.map((p) => [p.id, p]));
    return ids.map((id) => byId[id]).filter(Boolean);
  }, [stepNo, substeps]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Lock body scroll while modal is open.
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
    return undefined;
  }, [open]);

  if (items.length === 0) {
    // Still render the button — but signal that this step needs nothing new
    // and show a friendly message in the modal instead of an empty grid.
    return (
      <EmptyPartsButton stepNo={stepNo} open={open} setOpen={setOpen} />
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`flow-step-parts-btn-${stepNo}`}
        className="arcade-btn arcade-btn-ghost px-4 py-3 text-[11px] sm:text-xs"
      >
        <Wrench strokeWidth={3} className="h-4 w-4" />
        Parts used
        <span className="inline-flex h-5 min-w-[1.25rem] px-1 items-center justify-center rounded-full bg-[var(--arcade-yellow)] border-2 border-[var(--arcade-ink)] font-heading text-[10px] font-bold text-[var(--arcade-ink)]">
          {items.length}
        </span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-[var(--text-main)]/60 px-4 py-6"
            data-testid={`flow-step-parts-modal-${stepNo}`}
            onClick={() => setOpen(false)}
          >
            <motion.article
              initial={{ opacity: 0, y: 16, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.98 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              onClick={(e) => e.stopPropagation()}
              className="card-brut bg-white overflow-hidden w-full max-w-lg max-h-[90vh] flex flex-col"
            >
              <div className="flex items-center gap-3 p-5 sm:p-6 border-b-[3px] border-[var(--border-heavy)] bg-[var(--primary)]">
                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border-[3px] border-[var(--border-heavy)] bg-white shadow-[3px_3px_0px_var(--border-heavy)]">
                  <Wrench strokeWidth={2.5} className="h-5 w-5 text-[var(--text-main)]" />
                </span>
                <h2
                  className="flex-1 font-heading text-lg sm:text-xl font-semibold leading-snug text-[var(--text-main)]"
                  data-testid={`flow-step-parts-title-${stepNo}`}
                >
                  Parts used in Step {stepNo}
                </h2>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label="Close"
                  data-testid={`flow-step-parts-close-${stepNo}`}
                  className="arcade-btn arcade-btn-ghost arcade-btn-icon"
                >
                  <X strokeWidth={3} className="h-5 w-5" />
                </button>
              </div>

              <ul
                className="p-4 sm:p-5 grid grid-cols-1 sm:grid-cols-2 gap-3 overflow-y-auto"
                data-testid={`flow-step-parts-list-${stepNo}`}
              >
                {items.map((p) => (
                  <li
                    key={p.id}
                    className="flex items-center gap-3 rounded-xl border-2 border-[var(--border-heavy)] bg-white shadow-[3px_3px_0px_var(--border-heavy)] p-3"
                    data-testid={`flow-step-parts-item-${stepNo}-${p.id}`}
                  >
                    <div className="flex-shrink-0 w-20 h-20 sm:w-24 sm:h-24 bg-white flex items-center justify-center">
                      <img
                        src={p.img}
                        alt={p.label}
                        className="max-w-full max-h-full w-auto h-auto object-contain"
                        loading="lazy"
                        data-testid={`flow-step-parts-item-img-${stepNo}-${p.id}`}
                      />
                    </div>
                    <p
                      className={`flex-1 min-w-0 font-body font-bold text-[var(--text-main)] leading-snug [overflow-wrap:normal] [word-break:normal] hyphens-none pr-2 ${
                        p.label.length > 10
                          ? "text-xs sm:text-sm"
                          : "text-sm sm:text-base"
                      }`}
                      data-testid={`flow-step-parts-item-label-${stepNo}-${p.id}`}
                    >
                      {p.label}
                    </p>
                  </li>
                ))}
              </ul>
            </motion.article>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function EmptyPartsButton({ stepNo, open, setOpen }) {
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`flow-step-parts-btn-${stepNo}`}
        className="arcade-btn arcade-btn-ghost px-4 py-3 text-[11px] sm:text-xs"
      >
        <Wrench strokeWidth={3} className="h-4 w-4" />
        Parts used
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-[var(--text-main)]/60 px-4 py-6"
            data-testid={`flow-step-parts-modal-${stepNo}`}
            onClick={() => setOpen(false)}
          >
            <motion.article
              initial={{ opacity: 0, y: 16, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.98 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              onClick={(e) => e.stopPropagation()}
              className="card-brut bg-white overflow-hidden w-full max-w-md flex flex-col"
            >
              <div className="flex items-center gap-3 p-5 sm:p-6 border-b-[3px] border-[var(--border-heavy)] bg-[var(--primary)]">
                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border-[3px] border-[var(--border-heavy)] bg-white shadow-[3px_3px_0px_var(--border-heavy)]">
                  <Wrench strokeWidth={2.5} className="h-5 w-5 text-[var(--text-main)]" />
                </span>
                <h2
                  className="flex-1 font-heading text-lg sm:text-xl font-semibold leading-snug text-[var(--text-main)]"
                  data-testid={`flow-step-parts-title-${stepNo}`}
                >
                  Parts used in Step {stepNo}
                </h2>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label="Close"
                  data-testid={`flow-step-parts-close-${stepNo}`}
                  className="arcade-btn arcade-btn-ghost arcade-btn-icon"
                >
                  <X strokeWidth={3} className="h-5 w-5" />
                </button>
              </div>
              <p
                className="p-6 sm:p-8 text-center font-hand text-2xl text-[var(--text-main)]"
                data-testid={`flow-step-parts-empty-${stepNo}`}
              >
                No new hardware for this step.
              </p>
            </motion.article>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
